/**
 * Easing picker model (W2-05 easing-templates-raw; DESIGN.md §4.12, §5.7, §6.8, §6.11; U§5 A5): the grid layout,
 * keyboard navigation, RPE-number typing, search, context guards per EaseUse, recents and favourites, and the
 * per-segment skip count. Pure functions; the components render and wire them.
 *
 * Grid: a top row [Linear] [Stay, then jump], then one row per family with its plain strength word (Sine Gentle ...
 * Bounce Bouncy) and three columns: Out = "Slow end", In = "Slow start", InOut = "Slow both". QuintInOut and
 * ExpoInOut have no RPE number: their cells offer Phira's curve fitted as a Bezier, with the fit error.
 */

import { easeUseFor, trackInfo } from '../../core/commands/motionTrack';
import { Ease, EASING_CATALOG, HOLD, LINEAR } from '../../core/ease/index';
import type {
  BezierPoints,
  EaseFamily,
  EaseShapeWord,
  EaseStrength,
  EaseUse,
  EaseVariant,
  Ease as EaseValue,
  EasingCatalogEntry,
} from '../../core/ease/types';
import type { ChartDoc, DocIndexes, KeyId, LineProp } from '../../core/model/types';
import type { Json, MessageRef } from '../../core/util/types';
import type { ExperienceLevel } from '../../state/types';
import { m } from './easing.messages';

// ---------------------------------------------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------------------------------------------

export interface PickerCell {
  /** 'linear', 'hold', 'rpe:<easingType>' or 'approx:<family>'. */
  readonly id: string;
  readonly ease: EaseValue;
  /** Catalogue entry of a numbered curve (Linear included); null for Stay and the fitted curves. */
  readonly entry: EasingCatalogEntry | null;
  /** -1 = the top row (Linear, Stay). */
  readonly row: number;
  readonly col: number;
  /** Fitted Bezier cells: the curve they stand for and the largest difference (0..1 of the value range). */
  readonly approx?: { readonly family: 'quint' | 'expo'; readonly maxError: number };
}

export const GRID_STRENGTHS: readonly EaseStrength[] = [
  'gentle',
  'soft',
  'medium',
  'strong',
  'stronger',
  'extreme',
  'sharp',
  'overshoot',
  'springy',
  'bouncy',
];

const FAMILY_OF: Readonly<Record<EaseStrength, Exclude<EaseFamily, 'linear'>>> = {
  gentle: 'sine',
  soft: 'quad',
  medium: 'cubic',
  strong: 'quart',
  stronger: 'quint',
  extreme: 'expo',
  sharp: 'circ',
  overshoot: 'back',
  springy: 'elastic',
  bouncy: 'bounce',
};

/** Columns in display order: Slow end (Out), Slow start (In), Slow both (InOut). */
export const GRID_SHAPES: readonly EaseShapeWord[] = ['slowEnd', 'slowStart', 'slowBoth'];
const VARIANT_OF: Readonly<Record<EaseShapeWord, EaseVariant>> = { slowEnd: 'out', slowStart: 'in', slowBoth: 'inout' };

const TOP_ROW: readonly PickerCell[] = [
  { id: 'linear', ease: LINEAR, entry: Ease.catalog(1), row: -1, col: 0 },
  { id: 'hold', ease: HOLD, entry: null, row: -1, col: 1 },
];

function tween(id: number): EaseValue {
  return { kind: 'tween', id, left: 0, right: 1 };
}

function buildGrid(): readonly (readonly PickerCell[])[] {
  return GRID_STRENGTHS.map((strength, row) =>
    GRID_SHAPES.map((shape, col): PickerCell => {
      const family = FAMILY_OF[strength];
      const variant = VARIANT_OF[shape];
      const entry = EASING_CATALOG.find((e) => e.family === family && e.variant === variant) ?? null;
      if (entry !== null)
        return { id: `rpe:${String(entry.easingType)}`, ease: tween(entry.easingType), entry, row, col };
      const fam = family === 'quint' ? 'quint' : 'expo';
      const fit = Ease.approxBezier(fam, 'inout');
      return {
        id: `approx:${fam}`,
        ease: { kind: 'bezier', p: fit.p },
        entry: null,
        row,
        col,
        approx: { family: fam, maxError: fit.maxError },
      };
    }),
  );
}

let gridCache: readonly (readonly PickerCell[])[] | null = null;

/** The ten family rows (three cells each). */
export function pickerGrid(): readonly (readonly PickerCell[])[] {
  gridCache ??= buildGrid();
  return gridCache;
}

export function topRow(): readonly PickerCell[] {
  return TOP_ROW;
}

/** Every cell, top row first. */
export function allCells(): readonly PickerCell[] {
  return [...TOP_ROW, ...pickerGrid().flat()];
}

export function cellById(id: string): PickerCell | null {
  return allCells().find((c) => c.id === id) ?? null;
}

/** The cell showing an ease (whole numbered curves, Linear, Stay, the fitted curves), or null. */
export function cellForEase(e: EaseValue | null): PickerCell | null {
  if (e === null) return null;
  if (e.kind === 'hold') return cellById('hold');
  if (e.kind === 'bezier') return allCells().find((c) => c.ease.kind === 'bezier' && sameEase(c.ease, e)) ?? null;
  return e.id === 1 ? cellById('linear') : cellById(`rpe:${String(e.id)}`);
}

export function cellAt(row: number, col: number): PickerCell | null {
  if (row === -1) return TOP_ROW[col] ?? null;
  return pickerGrid()[row]?.[col] ?? null;
}

// ---------------------------------------------------------------------------------------------------------------
// Keyboard
// ---------------------------------------------------------------------------------------------------------------

export type NavKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End' | 'PageUp' | 'PageDown';

const LAST_ROW = GRID_STRENGTHS.length - 1;

function rowWidth(row: number): number {
  return row === -1 ? TOP_ROW.length : GRID_SHAPES.length;
}

/** The cell the arrow / Home / End / Page keys move to from (row, col). */
export function moveFocus(row: number, col: number, key: NavKey): { readonly row: number; readonly col: number } {
  const clampCol = (r: number, c: number): number => Math.min(Math.max(c, 0), rowWidth(r) - 1);
  switch (key) {
    case 'ArrowUp': {
      const r = Math.max(-1, row - 1);
      return { row: r, col: clampCol(r, col) };
    }
    case 'ArrowDown': {
      const r = Math.min(LAST_ROW, row + 1);
      return { row: r, col: clampCol(r, col) };
    }
    case 'ArrowLeft':
      if (col > 0) return { row, col: col - 1 };
      if (row > -1) return { row: row - 1, col: rowWidth(row - 1) - 1 };
      return { row, col };
    case 'ArrowRight':
      if (col < rowWidth(row) - 1) return { row, col: col + 1 };
      if (row < LAST_ROW) return { row: row + 1, col: 0 };
      return { row, col };
    case 'Home':
      return { row, col: 0 };
    case 'End':
      return { row, col: rowWidth(row) - 1 };
    case 'PageUp':
      return { row: -1, col: clampCol(-1, col) };
    case 'PageDown':
      return { row: LAST_ROW, col: clampCol(LAST_ROW, col) };
  }
}

/** Window in which a second digit completes a two-digit RPE number. */
export const DIGIT_WINDOW_MS = 400;

export interface DigitState {
  readonly digits: string;
  readonly at: number;
}

/**
 * Typing RPE numbers: a digit picks that number at once (1..9); a second digit within 400 ms makes a two-digit
 * number when one exists (1 then 2 -> 12, 2 then 9 -> 29); otherwise the second digit starts over. 0 alone picks
 * nothing.
 */
export function typeDigit(
  state: DigitState | null,
  digit: string,
  now: number,
): { readonly state: DigitState | null; readonly easingType: number | null } {
  if (!/^\d$/.test(digit)) return { state, easingType: null };
  if (state !== null && state.digits.length === 1 && now - state.at <= DIGIT_WINDOW_MS) {
    const two = Number(state.digits + digit);
    if (two >= 1 && two <= 29) return { state: null, easingType: two };
  }
  const one = Number(digit);
  return { state: { digits: digit, at: now }, easingType: one >= 1 && one <= 9 ? one : null };
}

/** Words that find the cells the catalogue has no entry for. */
const HOLD_WORDS = ['stay', 'hold', 'jump', 'step', 'constant', 'then'];

/** Search (`/`): numbers, names, initialisms and plain words ("oq", "out quad", "4", "soft slow end", "stay"). */
export function searchCells(query: string): PickerCell[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const out: PickerCell[] = [];
  const words = q.split(/\s+/);
  const hold = cellById('hold');
  if (hold !== null && words.some((w) => HOLD_WORDS.some((h) => h.startsWith(w) && w.length >= 2))) out.push(hold);
  for (const entry of Ease.search(q)) {
    const cell = entry.easingType === 1 ? cellById('linear') : cellById(`rpe:${String(entry.easingType)}`);
    if (cell !== null && !out.includes(cell)) out.push(cell);
  }
  // The fitted curves: "quint in out", "expo inout", "io quint".
  for (const fam of ['quint', 'expo'] as const) {
    const cell = cellById(`approx:${fam}`);
    const hit =
      words.some((w) => w.length >= 2 && fam.startsWith(w)) &&
      (q.includes('inout') || q.includes('in out') || q.includes('both') || q.includes('io'));
    if (cell !== null && hit && !out.includes(cell)) out.push(cell);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------------------------------------------

export type CellState =
  | { readonly kind: 'ok'; readonly warning?: MessageRef; readonly baked?: boolean }
  | { readonly kind: 'disabled'; readonly reason: MessageRef };

/** Whether a Bezier (custom curve) may be offered for a use. */
export function bezierAllowed(use: EaseUse): boolean {
  return use === 'general' || use === 'textRevealErase' || use === 'textOther';
}

/** Whether the "part of the curve" slice may be edited for a use and an ease. */
export function sliceAllowed(use: EaseUse, e: EaseValue): boolean {
  return use !== 'control' && e.kind === 'tween' && e.id !== 1;
}

/**
 * The state of an ease for a use (D§4.12 context guards): refused with the reason, allowed with a warning (text
 * overshoot, PH-16) or allowed and baked on export (eased speed in legacy mode).
 */
export function easeState(e: EaseValue, use: EaseUse): CellState {
  const why = Ease.validateFor(e, use);
  if (why !== null) return { kind: 'disabled', reason: why };
  if (use === 'textOther' && Ease.overshoots(e)) return { kind: 'ok', warning: { key: m.overshootWarning } };
  if (use === 'speedLegacy' && e.kind === 'tween' && e.id !== 1) return { kind: 'ok', baked: true };
  return { kind: 'ok' };
}

// ---------------------------------------------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------------------------------------------

/** What the picker needs to know about one segment. */
export interface SegmentInfo {
  readonly key: KeyId;
  readonly use: EaseUse;
  readonly prop: LineProp | null;
  readonly ease: EaseValue;
}

/**
 * The EaseUse of a segment (its start key): the motion commands' rule (speed mode, text reveal / erase, extra.json
 * variables), plus the Guided level's "speed in legacy mode: Linear and Stay only". Null when the key starts no
 * segment.
 */
export function segmentUse(doc: ChartDoc, idx: DocIndexes, key: KeyId, level: ExperienceLevel): EaseUse | null {
  const k = doc.keys.get(key);
  const nextId = idx.nextKey(key);
  const next = nextId === undefined ? undefined : doc.keys.get(nextId);
  if (k === undefined || next === undefined) return null;
  const info = trackInfo({ doc, idx }, k.track);
  if (info === null) return null;
  const use = easeUseFor({ doc, idx }, info, k.vOut, next.vIn);
  return use === 'speedLegacy' && level === 'guided' ? 'speedLegacyGuided' : use;
}

export function segmentInfos(
  doc: ChartDoc,
  idx: DocIndexes,
  keys: readonly KeyId[],
  level: ExperienceLevel,
): SegmentInfo[] {
  const out: SegmentInfo[] = [];
  for (const key of keys) {
    const use = segmentUse(doc, idx, key, level);
    const k = doc.keys.get(key);
    if (use === null || k === undefined) continue;
    const t = doc.tracks.get(k.track);
    out.push({ key, use, prop: t?.owner.kind === 'line' ? t.owner.prop : null, ease: k.ease });
  }
  return out;
}

/** How many of the segments an ease applies to, and the first reason the others refuse it. */
export function applyCount(
  segments: readonly SegmentInfo[],
  e: EaseValue,
): { readonly applies: number; readonly skipped: number; readonly reason: MessageRef | null } {
  let applies = 0;
  let skipped = 0;
  let reason: MessageRef | null = null;
  for (const s of segments) {
    const why = Ease.validateFor(e, s.use);
    if (why === null) applies++;
    else {
      skipped++;
      reason ??= why;
    }
  }
  return { applies, skipped, reason };
}

// ---------------------------------------------------------------------------------------------------------------
// Eases as JSON (recents, favourites, curve clipboard)
// ---------------------------------------------------------------------------------------------------------------

export const RECENTS_KEY = 'easing.recents';
export const FAVOURITES_KEY = 'easing.favourites';
export const MAX_RECENTS = 8;
export const MAX_FAVOURITES = 16;

/** Stable identity of an ease ('hold', 't:4:0:1', 'b:0.1,0.2,0.3,0.4'). */
export function easeKey(e: EaseValue): string {
  switch (e.kind) {
    case 'hold':
      return 'hold';
    case 'tween':
      return `t:${String(e.id)}:${String(e.left)}:${String(e.right)}`;
    case 'bezier':
      return `b:${e.p.join(',')}`;
  }
}

export function sameEase(a: EaseValue, b: EaseValue): boolean {
  return easeKey(a) === easeKey(b);
}

export function easeToJson(e: EaseValue): Json {
  switch (e.kind) {
    case 'hold':
      return { kind: 'hold' };
    case 'tween':
      return { kind: 'tween', id: e.id, left: e.left, right: e.right };
    case 'bezier':
      return { kind: 'bezier', p: [...e.p] };
  }
}

function isRecord(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A model ease from stored JSON, validated (null when malformed or not a valid curve). */
export function easeFromJson(j: unknown): EaseValue | null {
  if (!isRecord(j)) return null;
  let e: EaseValue | null = null;
  if (j.kind === 'hold') e = HOLD;
  else if (
    j.kind === 'tween' &&
    typeof j.id === 'number' &&
    typeof j.left === 'number' &&
    typeof j.right === 'number'
  ) {
    e = { kind: 'tween', id: j.id, left: j.left, right: j.right };
  } else if (j.kind === 'bezier' && Array.isArray(j.p) && j.p.length === 4 && j.p.every((v) => typeof v === 'number')) {
    const p = j.p;
    const points: BezierPoints = [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0, p[3] ?? 0];
    e = { kind: 'bezier', p: points };
  }
  if (e === null) return null;
  return Ease.validateFor(e, 'general') === null ? e : null;
}

/** Eases stored under a settings key (invalid entries dropped). */
export function easeList(raw: Json | undefined): EaseValue[] {
  if (!Array.isArray(raw)) return [];
  const out: EaseValue[] = [];
  for (const item of raw as readonly Json[]) {
    const e = easeFromJson(item);
    if (e !== null && !out.some((x) => sameEase(x, e))) out.push(e);
  }
  return out;
}

/** The recents after using `e` (most recent first, no duplicates, at most MAX_RECENTS). */
export function pushRecent(list: readonly EaseValue[], e: EaseValue): EaseValue[] {
  return [e, ...list.filter((x) => !sameEase(x, e))].slice(0, MAX_RECENTS);
}

/** Favourites with `e` toggled (added at the end, at most MAX_FAVOURITES). */
export function toggleFavourite(list: readonly EaseValue[], e: EaseValue): EaseValue[] {
  if (list.some((x) => sameEase(x, e))) return list.filter((x) => !sameEase(x, e));
  return [...list, e].slice(-MAX_FAVOURITES);
}

/** The whole curve of a sliced numbered ease (favourites and the grid compare whole curves). */
export function wholeCurve(e: EaseValue): EaseValue {
  return e.kind === 'tween' ? { kind: 'tween', id: e.id, left: 0, right: 1 } : e;
}
